import { useState, useEffect, useCallback, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { trackSignUpCompletion } from "@/lib/signupTracking";
import { trackEvent } from "@/lib/analytics";
import { sendWelcomeEmailIfNeeded } from "@/lib/welcome-email.functions";

/**
 * Fire-and-forget welcome email. The server function is the authority on
 * whether one is owed (it claims profiles.welcome_email_sent_at first), so a
 * repeat call is harmless; this ref just avoids needless round trips.
 */
const welcomeAttempted = new Set<string>();
function maybeSendWelcomeEmail(userId: string) {
  if (welcomeAttempted.has(userId)) return;
  welcomeAttempted.add(userId);
  void sendWelcomeEmailIfNeeded().catch(() => {
    welcomeAttempted.delete(userId);
  });
}

export type AppRole = "admin" | "user" | "founding_member" | "paid_member";


interface AuthState {
  user: User | null;
  session: Session | null;
  roles: AppRole[];
  isLoading: boolean;
  /** True until the first role resolution (cache seed or server) for the user. */
  rolesResolved: boolean;
  /** True only once roles were confirmed by a real server fetch (not cache). */
  rolesVerified: boolean;
  isAdmin: boolean;
  isFoundingMember: boolean;
  isPaidMember: boolean;
}

/** Last-known-good roles cache key (survives refreshes / transient failures). */
const rolesCacheKey = (userId: string) => `statlign_roles_${userId}`;

function readCachedRoles(userId: string): AppRole[] | null {
  try {
    const raw = localStorage.getItem(rolesCacheKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AppRole[]) : null;
  } catch {
    return null;
  }
}

function writeCachedRoles(userId: string, roles: AppRole[]) {
  try {
    localStorage.setItem(rolesCacheKey(userId), JSON.stringify(roles));
  } catch {
    /* ignore quota / private-mode errors */
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const ROLES_TTL_MS = 5 * 60_000;
const rolesMemo = new Map<string, { at: number; p: Promise<AppRole[] | null> }>();

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    roles: [],
    isLoading: true,
    rolesResolved: false,
    rolesVerified: false,
    isAdmin: false,
    isFoundingMember: false,
    isPaidMember: false,
  });

  // Tracks the latest user id we're resolving roles for, so stale async
  // responses (e.g. after a fast sign-out/in) never overwrite fresh state.
  const activeUserId = useRef<string | null>(null);

  /**
   * Fetch ALL roles for a user with retry. A user may hold several roles
   * (e.g. admin + founding_member). Returns `null` on hard failure so callers
   * can keep the last-known-good roles instead of wrongly downgrading admins.
   */
  const fetchUserRoles = useCallback(
    (userId: string, attempts = 3): Promise<AppRole[] | null> => {
      // Share one role lookup across every component using this hook (5 min).
      const hit = rolesMemo.get(userId);
      if (hit && Date.now() - hit.at < ROLES_TTL_MS) return hit.p;
      const p = (async () => {
        for (let i = 0; i < attempts; i++) {
          const { data, error } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", userId);
          if (!error) {
            return (data ?? []).map((r) => r.role as AppRole);
          }
          console.error(`Error fetching user roles (attempt ${i + 1}):`, error);
          if (i < attempts - 1) await sleep(400 * (i + 1));
        }
        rolesMemo.delete(userId);
        return null;
      })();
      rolesMemo.set(userId, { at: Date.now(), p });
      return p;
    },
    [],
  );

  /**
   * Apply resolved roles. `verified` is true only when `roles` came from a real
   * server fetch. Admin gating (destructive UI) requires verification, so a
   * spoofed/stale localStorage cache can never render admin controls; the cache
   * is only used to keep non-destructive UX (founding/paid) from flickering.
   */
  const applyRoles = useCallback((roles: AppRole[], verified: boolean) => {
    setState((prev) => ({
      ...prev,
      roles,
      rolesResolved: true,
      rolesVerified: verified || prev.rolesVerified,
      isAdmin: verified && roles.includes("admin"),
      isFoundingMember: roles.includes("founding_member"),
      isPaidMember: roles.includes("paid_member"),
    }));
  }, []);

  /**
   * Resolve roles for a user: seed instantly from the last-known-good cache
   * (so members never flicker), then fetch fresh. Admin status is applied only
   * from the server fetch. On a hard fetch failure keep the cached roles rather
   * than clobbering to empty.
   */
  const resolveRoles = useCallback(
    async (userId: string) => {
      const cached = readCachedRoles(userId);
      if (cached && cached.length > 0) applyRoles(cached, false);

      const fresh = await fetchUserRoles(userId);
      if (activeUserId.current !== userId) return; // stale response, ignore

      if (fresh !== null) {
        writeCachedRoles(userId, fresh);
        applyRoles(fresh, true);
      } else if (!cached) {
        // No cache and the fetch failed — mark resolved so the UI can proceed,
        // but keep roles empty (will re-resolve on next auth event).
        setState((prev) => ({ ...prev, rolesResolved: true }));
      }
    },
    [applyRoles, fetchUserRoles],
  );

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        const nextUserId = session?.user?.id ?? null;
        // Returning to the tab fires TOKEN_REFRESHED / SIGNED_IN for the same user.
        // Store the fresh session quietly without re-running role checks, so gated
        // content does not flash or re-render.
        if (
          nextUserId &&
          nextUserId === activeUserId.current &&
          (_event === "TOKEN_REFRESHED" || _event === "SIGNED_IN" || _event === "USER_UPDATED")
        ) {
          setState((prev) => (prev.session?.access_token === session?.access_token ? prev : { ...prev, session, user: session?.user ?? prev.user }));
          return;
        }
        activeUserId.current = nextUserId;

        setState((prev) => ({
          ...prev,
          session,
          user: session?.user ?? null,
          isLoading: false,
          // Only reset resolution when the actual user identity changes.
          rolesResolved: nextUserId === prev.user?.id ? prev.rolesResolved : false,
          rolesVerified: nextUserId === prev.user?.id ? prev.rolesVerified : false,
        }));

        // Defer role fetch with setTimeout to avoid deadlock inside the callback.
        if (session?.user) {
          setTimeout(() => resolveRoles(session.user.id), 0);
          setTimeout(() => maybeSendWelcomeEmail(session.user.id), 0);
          setTimeout(() => trackSignUpCompletion(session.user), 0);
        } else {

          setState((prev) => ({
            ...prev,
            roles: [],
            rolesResolved: true,
            rolesVerified: true,
            isAdmin: false,
            isFoundingMember: false,
            isPaidMember: false,
          }));
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      const nextUserId = session?.user?.id ?? null;
      activeUserId.current = nextUserId;
      setState((prev) => ({
        ...prev,
        session,
        user: session?.user ?? null,
        isLoading: false,
      }));

      if (session?.user) {
        resolveRoles(session.user.id);
        maybeSendWelcomeEmail(session.user.id);
        trackSignUpCompletion(session.user);


      } else {
        setState((prev) => ({ ...prev, rolesResolved: true, rolesVerified: true }));
      }
    });

    return () => subscription.unsubscribe();
  }, [resolveRoles]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    trackEvent(error ? "login_failed" : "login", { method: "email" });
    return { error };
  };

  const signUp = async (
    email: string,
    password: string,
    refCode?: string | null,
    dateOfBirth?: string | null,
  ) => {
    // Keep the page the visitor was heading to (e.g. agent consent) after
    // they confirm their email.
    const next = new URLSearchParams(window.location.search).get("redirect");
    const redirectUrl =
      next && next.startsWith("/") && !next.startsWith("//")
        ? `${window.location.origin}/auth?redirect=${encodeURIComponent(next)}`
        : `${window.location.origin}/`;

    const data: Record<string, string> = {};
    if (refCode) data["ref_code"] = refCode;
    if (dateOfBirth) data["date_of_birth"] = dateOfBirth;

    const { data: result, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        ...(Object.keys(data).length ? { data } : {}),
      },
    });
    // The `sign_up` conversion is fired once the account actually has a session
    // (see trackSignUpCompletion in the auth listener), so email and Google
    // sign-ups are counted the same way and never double-counted here.
    return { error, session: result?.session ?? null };

  };

  const signOut = async () => {
    // Clear cached roles for the current user on explicit sign-out.
    if (state.user?.id) {
      try {
        localStorage.removeItem(rolesCacheKey(state.user.id));
      } catch {
        /* ignore */
      }
    }
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  return {
    ...state,
    // Back-compat: expose a single primary role
    role: state.roles.includes("admin")
      ? ("admin" as AppRole)
      : state.roles[0] ?? null,
    signIn,
    signUp,
    signOut,
  };
}
